import "../i18n/config";
import React, { FunctionComponent, useEffect, useMemo } from "react";
import styles from "./ChatComponent.module.scss";
import { useDependencyInjector } from "@whatslly/commons/di";
import type { AvailableTabs } from "./stores/ChatMessagesStore";
import { ChatMessagesStore } from "./stores/ChatMessagesStore";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { useDeprecatedGlobalStyles } from "@whatslly/ui-components/styles/useTheme";
import { mergeRefs } from "react-merge-refs";
import { PersonalTab } from "./tabs/PeronalTab/PersonalTab";
import { GroupsTab } from "./tabs/GroupsTab/GroupsTab";
import { ButtonSet } from "@whatslly/ui-components/v2/BaseElements/ButtonSet";
import { TuvisSkeleton } from "@whatslly/ui-components/TuvisSkeleton";
import upperCase from "lodash/upperCase";
import { FilterSettingsType } from "@whatslly/ui-components/EntityFilter";
import type { MessageFloatingActionType } from "./components/Message/Components/FloatingActions";
import { MessageInlineActionType } from "@whatsllyorg/crm-components/ChatComponent/components/Message/Components/InlineActions";

export const ChatComponent = observer<
  {
    ignoreDarkMode?: boolean;
    allowedToDownload?: boolean;
    hideGroupsTab?: boolean;
    hideDateFilter?: boolean;
    filtersSettings?: FilterSettingsType[];
    FilterComponent?: React.FC;
    GroupFilterComponent?: React.FC;
    isLoadingBasedOn?: "groups" | "personal";
    hideParticipants?: boolean;
    enableDateRangeFilter?: boolean;
    isSidecarsComponent?: boolean;
    floatingActions?: [MessageFloatingActionType] | [MessageFloatingActionType, MessageFloatingActionType];
    inlineActions?: MessageInlineActionType[];
    openCalendarInTheMiddle?: boolean;
    limitDateRange?: number;
  },
  HTMLDivElement
>(
  React.forwardRef((props, ref) => {
    const chatMessagesStore = useDependencyInjector(ChatMessagesStore);
    const localRef = React.useRef<HTMLDivElement>(undefined);
    const { t } = useTranslation();

    const TABS: {
      key: AvailableTabs;
      value: string;
      Component: FunctionComponent<{
        allowedToDownload?: boolean;
        hideDateFilter?: boolean;
        filtersSettings?: FilterSettingsType[];
        FilterComponent?: React.FC;
        hideParticipants?: boolean;
        enableDateRangeFilter?: boolean;
        GroupFilterComponent?: React.FC;
        isSidecarsComponent?: boolean;
        floatingActions?: [MessageFloatingActionType] | [MessageFloatingActionType, MessageFloatingActionType];
        inlineActions?: MessageInlineActionType[];
        openCalendarInTheMiddle?: boolean;
        limitDateRange?: number;
      }>;
    }[] = useMemo(() => {
      return [
        {
          Component: PersonalTab,
          key: "personal",
          value: upperCase(t("chat_component.personal")),
          testId: "personal-tab",
        },
        {
          Component: GroupsTab,
          key: "groups",
          value: `${upperCase(t("chat_component.groups"))}${
            chatMessagesStore.groups.state === "fulfilled" && chatMessagesStore.allowSyncGroups
              ? ` (${chatMessagesStore.groups.value.groups.length})`
              : ""
          }`,
          locked: !chatMessagesStore.allowSyncGroups,
          lockedToolTip: t("chat_component.groups_locked_tooltip"),
          testId: "groups-tab",
        },
      ];
    }, [chatMessagesStore.groups.state, chatMessagesStore.groups.value, t, chatMessagesStore.allowSyncGroups]);

    const activeTab = useMemo(
      () => TABS.find((item) => item.key === chatMessagesStore.selectedTab) ?? TABS[0],
      [chatMessagesStore.selectedTab, TABS]
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => chatMessagesStore.setSelectedTab(`personal`), []);

    useDeprecatedGlobalStyles({ element: !props.ignoreDarkMode ? localRef.current : undefined });

    return (
      <div ref={mergeRefs([localRef, ref])} className={styles.tuvisAppContainer}>
        <div className={styles.messageHeader}>
          <div className={styles.syncedMessagesTitle}>
            {t("chat_component.synced_messages")} <span className={styles.byTuvis}>({t("chat_component.by_tuvis")})</span>
          </div>
          {!props.hideGroupsTab && (
            <TuvisSkeleton
              height={`33px`}
              width={`190px`}
              loading={
                props.isLoadingBasedOn === "personal"
                  ? chatMessagesStore.messagesFiltered.state === "pending"
                  : chatMessagesStore.groups.state === "pending"
              }
            >
              <ButtonSet<{ key: AvailableTabs; value: string }>
                testId={`switch-table-mode`}
                className={styles.tabs}
                items={TABS}
                onClick={(item) => chatMessagesStore.setSelectedTab(item.key)}
                activeItem={activeTab}
              />
            </TuvisSkeleton>
          )}
        </div>
        <activeTab.Component
          allowedToDownload={props.allowedToDownload}
          hideDateFilter={props.hideDateFilter}
          filtersSettings={props.filtersSettings}
          FilterComponent={props.FilterComponent}
          hideParticipants={props.hideParticipants}
          GroupFilterComponent={props.GroupFilterComponent}
          enableDateRangeFilter={props.enableDateRangeFilter}
          isSidecarsComponent={props.isSidecarsComponent}
          floatingActions={props.floatingActions}
          inlineActions={props.inlineActions}
          openCalendarInTheMiddle={props.openCalendarInTheMiddle}
          limitDateRange={props.limitDateRange}
        />
      </div>
    );
  })
);
